import { Injectable } from '@angular/core';
import { catchError, EMPTY, filter, first, from, map, mergeMap, Observable, switchMap, take, throwError } from 'rxjs';

import { Actions, CanvasCommunicationService, IMergeFilesService, MergeFileItem, Store } from '@olympus/ngx-shared';
import { ToastService } from 'shared-modules-angular/toast';
import { isolateFileNameWithExtension } from 'shared-utils/file/file.helper';
import { CommandGetFilesPreview, CommandGetFilesPreviewError, CommandMergeFiles, CommandMergeFilesResponse, CommandMergeFilesResponseError } from 'ut-business/communication';

import { DPUActionType } from '../../reducers/dpu/dpu.reducer';
import { FileActionType } from '../../reducers/file-dialog/file-action-type.enum';

@Injectable({
  providedIn: 'root',
})
export class MergeFilesService implements IMergeFilesService {
  constructor(
    private readonly _store: Store,
    private readonly _actions$: Actions,
    private readonly _communicationService: CanvasCommunicationService,
    private readonly _toastService: ToastService,
  ) { }

  public selectFile(): Observable<MergeFileItem> {
    this._store.dispatch({ type: FileActionType.SELECT_FILE_TO_MERGE_DIALOG });

    return this._actions$.pipe(
      filter(action => action.type === FileActionType.FILE_TO_MERGE_SELECTED),
      first(),
      map(action => action.payload),
      switchMap(filePaths => this.loadFilePreview(filePaths)),
      mergeMap(filePreviews => from(filePreviews)),
    );
  }

  public selectSavePath(defaultName: string): Observable<string | null> {
    this._store.dispatch({
      type: FileActionType.MERGE_FILE_SAVE_DIALOG,
      payload: { defaultName, fileFilter: { name: 'Evident files', extensions: ['nde'] } },
    });

    return this._actions$.pipe(
      filter(action =>
        action.type === FileActionType.MERGE_FILE_SAVE_PATH_SELECTED
        || action.type === FileActionType.MERGE_FILE_SAVE_DIALOG_CANCELLED,
      ),
      first(),
      map(action => action.type === FileActionType.MERGE_FILE_SAVE_PATH_SELECTED ? action.payload as string : null),
    );
  }

  public startMerge(files: MergeFileItem[]): Observable<CommandMergeFilesResponse> {
    return this.selectSavePath('').pipe(
      switchMap((destinationPath) => {
        if (!destinationPath) {
          return EMPTY;
        }

        return this.mergeFiles(files, destinationPath);
      }),
    );
  }

  public mergeFiles(files: MergeFileItem[], destinationPath: string): Observable<CommandMergeFilesResponse> {
    const mergeFilesRequest = files
      .filter(file => !!file.path)
      .map(file => ({
        pathFileName: file.path ?? '',
        ...(file.groups && file.groups.length > 0 && {
          selectedGroups: file.groups.map(group => ({
            id: group.id,
            outputName: group.name,
          })),
        }),
      }));

    return this._communicationService.communication.sendCommand(new CommandMergeFiles({
      output: {
        pathFileName: destinationPath,
        overwrite: true,
      },
      files: mergeFilesRequest,
    })).pipe(
      map(() => ({ destinationPathFileName: destinationPath })),
      catchError((error: CommandMergeFilesResponseError) => {
        const fileDescription = 'data' in error
          && typeof error.data === 'object'
          && Array.isArray(error.data.files)
          ? (error.data.files[0]?.description ?? error.message)
          : error.message;

        this._toastService.error({
          title: 'Unable to merge files',
          description: fileDescription || 'An unexpected error occurred while merging files.',
        });

        return throwError(() => error);
      }),
    );
  }

  public openFile(path: string): void {
    this._store.dispatch({ type: DPUActionType.FILE_SELECTED, payload: path });
  }

  private loadFilePreview(
    filePaths: string[],
  ): Observable<MergeFileItem[]> {
    return this._communicationService.communication
      .sendCommand(
        new CommandGetFilesPreview({
          files: filePaths.map(pathFileName => ({
            pathFileName,
          })),
        }),
      )
      .pipe(
        take(1),
        catchError((error: CommandGetFilesPreviewError | Error) => {
          if ('data' in error) {
            error.data.files?.forEach((fileError) => {
              this._toastService.error({
                title: 'Error',
                description:
                  fileError.description
                  ?? 'Could not preview the selected file.',
              });
            });
          } else {
            this._toastService.error({
              title: 'Error',
              description: error.message,
            });
          }

          return EMPTY;
        }),
        map(({ files = [] }) => filePaths.map((path) => {
          const preview = files.find(file => file.pathFileName === path);
          const name = isolateFileNameWithExtension(path);

          return {
            name,
            size: preview?.fileSize ?? 0,
            path,
            groups:
              preview?.config?.setup?.groups?.map(group => ({
                id: group.id,
                name: group.name ?? `Group ${group.id}`,
              })) ?? [],
            memoryUsage: preview?.estimatedMemoryUsage ?? 0,
          };
        })),
      );
  }
}
